import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

// Reusable Legal Page Layout
const LegalPageLayout = ({ title, children }) => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Link 
              to="/tyron" 
              className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Back to Portfolio</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-white mb-8">{title}</h1>
          <div className="prose prose-invert prose-lg max-w-none">
            {children}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-gray-500 text-sm">© {new Date().getFullYear()} Tyron. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

// Privacy Policy
export const TyronPrivacy = () => (
  <LegalPageLayout title="Privacy Policy">
    <p className="text-gray-400 mb-4">Last updated: {new Date().toLocaleDateString()}</p>
    
    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">1. Information We Collect</h2>
    <p className="text-gray-300 mb-4">
      When you visit this portfolio website, we may collect certain information automatically, including:
    </p>
    <ul className="list-disc pl-6 text-gray-300 space-y-2">
      <li>Browser type and version</li>
      <li>Operating system</li>
      <li>Pages visited and time spent</li>
      <li>Referral source</li>
    </ul>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">2. How We Use Information</h2>
    <p className="text-gray-300 mb-4">
      Any information collected is used solely to improve the website experience and understand visitor behavior.
    </p>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">3. Contact Form</h2>
    <p className="text-gray-300 mb-4">
      If you submit information through the contact form, your data will be used only to respond to your inquiry 
      and will not be shared with third parties.
    </p>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">4. Third-Party Links</h2>
    <p className="text-gray-300 mb-4">
      This website contains links to external sites (such as LinkedIn). We are not responsible for the privacy 
      practices of these external sites.
    </p>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">5. Contact</h2>
    <p className="text-gray-300">
      For privacy-related questions, please contact: <span className="text-blue-400">tyron@spirit.com</span>
    </p>
  </LegalPageLayout>
);

// Terms of Service
export const TyronTerms = () => (
  <LegalPageLayout title="Terms of Service">
    <p className="text-gray-400 mb-4">Last updated: {new Date().toLocaleDateString()}</p>
    
    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">1. Acceptance of Terms</h2>
    <p className="text-gray-300 mb-4">
      By accessing this portfolio website, you agree to be bound by these Terms of Service and all applicable 
      laws and regulations.
    </p>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">2. Use License</h2>
    <p className="text-gray-300 mb-4">
      Permission is granted to temporarily view the materials on this website for personal, non-commercial use only. 
      This is the grant of a license, not a transfer of title.
    </p>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">3. Intellectual Property</h2>
    <p className="text-gray-300 mb-4">
      All content on this website, including text, graphics, logos, and images, is the property of Tyron and 
      is protected by intellectual property laws.
    </p>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">4. Disclaimer</h2>
    <p className="text-gray-300 mb-4">
      The materials on this website are provided on an 'as is' basis. No warranties, expressed or implied, are made 
      regarding the accuracy or reliability of the information presented.
    </p>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">5. Limitations</h2>
    <p className="text-gray-300">
      In no event shall Tyron be liable for any damages arising out of the use or inability to use the materials 
      on this website.
    </p>
  </LegalPageLayout>
);

// Disclaimer
export const TyronDisclaimer = () => (
  <LegalPageLayout title="Disclaimer">
    <p className="text-gray-400 mb-4">Last updated: {new Date().toLocaleDateString()}</p>
    
    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">General Disclaimer</h2>
    <p className="text-gray-300 mb-4">
      The information provided on this portfolio website is for general informational purposes only. All information 
      is provided in good faith, however, no representation or warranty of any kind, express or implied, is made 
      regarding the accuracy, adequacy, validity, reliability, availability, or completeness of any information.
    </p>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">Professional Information</h2>
    <p className="text-gray-300 mb-4">
      The professional experience, skills, and qualifications presented on this website are accurate to the best 
      of my knowledge. Past performance and experience do not guarantee future results.
    </p>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">External Links</h2>
    <p className="text-gray-300 mb-4">
      This website may contain links to external websites. These links are provided for convenience and informational 
      purposes only; they do not constitute an endorsement or approval of any products, services, or opinions 
      contained therein.
    </p>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">Contact</h2>
    <p className="text-gray-300">
      For questions about this disclaimer, please contact: <span className="text-blue-400">tyron@spirit.com</span>
    </p>
  </LegalPageLayout>
);

// AI Usage Policy
export const TyronAIPolicy = () => (
  <LegalPageLayout title="AI Usage Policy">
    <p className="text-gray-400 mb-4">Last updated: {new Date().toLocaleDateString()}</p>
    
    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">AI-Assisted Development</h2>
    <p className="text-gray-300 mb-4">
      This portfolio website was developed with the assistance of artificial intelligence tools. AI was used to 
      help with code generation, design implementation, and content structuring.
    </p>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">Human Oversight</h2>
    <p className="text-gray-300 mb-4">
      All AI-generated content and code has been reviewed, edited, and approved by humans. The professional 
      experience, skills, and biographical information presented are accurate and human-verified.
    </p>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">Content Authenticity</h2>
    <p className="text-gray-300 mb-4">
      While AI tools assisted in the technical development of this website, the professional narrative, 
      career experiences, and personal information are authentic and represent real achievements.
    </p>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">AI Training Restriction</h2>
    <p className="text-gray-300">
      The content of this website may not be used for training artificial intelligence models or machine 
      learning algorithms without explicit written permission from the website owner.
    </p>
  </LegalPageLayout>
);

// Data Processing Addendum
export const TyronDPA = () => (
  <LegalPageLayout title="Data Processing Addendum">
    <p className="text-gray-400 mb-4">Last updated: {new Date().toLocaleDateString()}</p>
    
    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">1. Purpose</h2>
    <p className="text-gray-300 mb-4">
      This Data Processing Addendum outlines how personal data is processed when you interact with this website.
    </p>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">2. Data Controller</h2>
    <p className="text-gray-300 mb-4">
      Tyron acts as the data controller for any personal information collected through this portfolio website.
    </p>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">3. Types of Data Processed</h2>
    <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
      <li>Contact form submissions (name, email, message)</li>
      <li>Analytics data (anonymized)</li>
      <li>Technical data (browser, device type)</li>
    </ul>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">4. Legal Basis</h2>
    <p className="text-gray-300 mb-4">
      Data processing is based on legitimate interest (website operation) and consent (contact form submissions).
    </p>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">5. Data Retention</h2>
    <p className="text-gray-300 mb-4">
      Contact form data is retained only as long as necessary to respond to inquiries. Analytics data is 
      anonymized and aggregated.
    </p>

    <h2 className="text-2xl font-bold text-blue-400 mt-8 mb-4">6. Your Rights</h2>
    <p className="text-gray-300 mb-4">
      You have the right to access, rectify, or delete any personal data we hold about you. Contact us at 
      <span className="text-blue-400"> tyron@spirit.com</span> to exercise these rights.
    </p>
  </LegalPageLayout>
);

export default { TyronPrivacy, TyronTerms, TyronDisclaimer, TyronAIPolicy, TyronDPA };
